import {useEffect,useState} from 'react';
type Choice={id:string;name:string};
type Location={code:string;name:string;timezone:string};
type OwnEntry={id:string;date:string;serviceName:string|null;version:number};
const toMinute=(value:string)=>Number(value.slice(0,2))*60+Number(value.slice(3,5));
export function PublicWaitlistPanel({location,date,isGuestAccount,onLogin,onClose}:{
  location:Location;date:string;isGuestAccount:boolean;onLogin:()=>void;
  onClose:()=>void;
}){
  const [chosenDate,setChosenDate]=useState(date);
  const [options,setOptions]=useState<{rooms:Choice[];services:Choice[]}|null>(null);
  const [rooms,setRooms]=useState<string[]>([]);
  const [service,setService]=useState('');
  const [guests,setGuests]=useState(0);
  const [start,setStart]=useState('10:00');
  const [end,setEnd]=useState('22:00');
  const [notifyEmail,setNotifyEmail]=useState(true);
  const [notifySms,setNotifySms]=useState(true);
  const [busy,setBusy]=useState(false);
  const [status,setStatus]=useState('');
  const [saved,setSaved]=useState<OwnEntry[]>([]);
  useEffect(()=>{
    const ctrl=new AbortController();
    setStatus('');
    void fetch('/api/v1/public/waitlist/options?'+
      new URLSearchParams({location:location.code}),{
        cache:'no-store',signal:ctrl.signal,
      }).then(async response=>{
        const body=await response.json() as {ok:boolean;data?:{rooms:Choice[];services:Choice[]}};
        if(!response.ok||!body.ok||!body.data)throw Error('Waitlist options unavailable');
        if(ctrl.signal.aborted)return;
        setOptions(body.data);
        setRooms(body.data.rooms.filter(x=>x.name.toLowerCase().includes('float')).map(x=>x.id));
      }).catch(()=>{if(!ctrl.signal.aborted)setStatus('Room and service options unavailable.')});
    return ()=>ctrl.abort();
  },[location.code]);
  useEffect(()=>{
    if(!isGuestAccount)return;
    const ctrl=new AbortController();
    void fetch('/api/v1/guest/waitlist?'+new URLSearchParams({
      location:location.code}),{credentials:'same-origin',cache:'no-store',signal:ctrl.signal})
      .then(async response=>{
        const body=await response.json() as {ok:boolean;data?:{entries:OwnEntry[]}};
        if(response.ok&&body.ok&&!ctrl.signal.aborted)setSaved(body.data?.entries??[]);
      }).catch(()=>{});
    return ()=>ctrl.abort();
  },[location.code,isGuestAccount]);
  const submit=async()=>{
    if(!options||busy)return;
    // A verified linked employee/customer is one valid person, too. The
    // server session decides; a real 401 opens the existing login form.
    setBusy(true);setStatus('');
    try{
      const response=await fetch('/api/v1/guest/waitlist',{
        method:'POST',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:location.code,draft:{
          date:chosenDate,fromMinute:toMinute(start),toMinute:toMinute(end),
          resourceIds:rooms,serviceId:service||null,additionalGuests:guests,
          priority:'NORMAL',notes:'',notifyEmail,notifySms,
        }}),
      });
      const body=await response.json() as {ok:boolean;data?:{id:string;version:number};
        error?:{code:string}};
      if(response.status===401){onLogin();throw Error('Sign in to join waitlist')}
      if(!response.ok||!body.ok||!body.data)
        throw Error(body.error?.code??'Unable to join waitlist');
      setStatus('Added to waitlist. Contact preferences are saved; automated notifications are not yet enabled.');
      setSaved(prev=>[...prev,{id:body.data!.id,version:body.data!.version,
        date:chosenDate,serviceName:options.services.find(x=>x.id===service)?.name??null}]);
    }catch(e){setStatus(e instanceof Error?e.message:'Unable to join waitlist')}
    finally{setBusy(false)}
  };
  const remove=async(entry:OwnEntry)=>{
    setBusy(true);setStatus('');
    try{
      const response=await fetch('/api/v1/guest/waitlist/'+
        encodeURIComponent(entry.id),{method:'DELETE',credentials:'same-origin',
          headers:{'content-type':'application/json'},
          body:JSON.stringify({version:entry.version})});
      if(!response.ok)throw Error('Unable to remove waitlist entry');
      setSaved(prev=>prev.filter(x=>x.id!==entry.id));
      setStatus('Removed from waitlist.');
    }catch(e){setStatus(e instanceof Error?e.message:'Unable to remove waitlist entry')}
    finally{setBusy(false)}
  };
  return <section className="ecfs-public-waitlist" aria-label="Join the Waitlist">
    <button type="button" className="ecfs-link" onClick={onClose}>‹ Back to booking</button>
    <h2>Join the East Coast Float Spa {location.name} Waitlist</h2>
    <form onSubmit={e=>{e.preventDefault();void submit()}}>
      <label>Number of Guests
        <span>Me and <select aria-label="Additional waitlist guests" value={guests}
          onChange={e=>setGuests(Number(e.target.value))}>
          {Array.from({length:11},(_,i)=><option value={i} key={i}>{i} guests</option>)}
        </select></span>
      </label>
      <fieldset><legend>Choose Float Rooms and Rooms</legend>
        {options?.rooms.map(room=><label key={room.id} className="ecfs-public-waitlist-room">
          <input type="checkbox" checked={rooms.includes(room.id)}
            onChange={e=>setRooms(prev=>e.target.checked?
              [...prev,room.id]:prev.filter(id=>id!==room.id))}/>{room.name}
        </label>)}
      </fieldset>
      <label>Choose Service
        <select aria-label="Choose Waitlist Service" value={service}
          onChange={e=>setService(e.target.value)}>
          <option value="">Any Float Service</option>
          {options?.services.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </label>
      <label>Appointments on…
        <input type="date" aria-label="Waitlist date" value={chosenDate}
          min={new Intl.DateTimeFormat("en-CA",{timeZone:location.timezone,
            year:"numeric",month:"2-digit",day:"2-digit"}).format(new Date())}
          onChange={e=>setChosenDate(e.target.value)}/>
      </label>
      <fieldset><legend>Appointments starting between…</legend>
        <div className="ecfs-waitlist-time-row">
          <input type="time" aria-label="Earliest acceptable appointment"
            value={start} onChange={e=>setStart(e.target.value)}/>
          <span>and</span>
          <input type="time" aria-label="Latest acceptable appointment"
            value={end} onChange={e=>setEnd(e.target.value)}/>
        </div>
      </fieldset>
      <button type="submit" disabled={busy||!options||toMinute(end)<=toMinute(start)}>
        {busy?'Adding…':'Add Me To Waitlist'}</button>
      <p className="ecfs-waitlist-alert">
        <label><input type="checkbox" checked={notifyEmail}
          onChange={e=>setNotifyEmail(e.target.checked)}/>Notify me by email</label>
        <label><input type="checkbox" checked={notifySms}
          onChange={e=>setNotifySms(e.target.checked)}/>Notify me by text message</label>
      </p>
      <p>These communication preferences are saved for future use.
        Automatic waitlist emails and texts have not yet been enabled.</p>
    </form>
    {status&&<p role="status">{status}</p>}
    {saved.length>0&&<section aria-label="Your waitlist entries">
      <h3>Your waitlist entries</h3>
      {saved.map(entry=><div key={entry.id}>
        {entry.date} · {entry.serviceName??'Any service'}
        <button type="button" onClick={()=>void remove(entry)} disabled={busy}>
          Remove</button>
      </div>)}
    </section>}
  </section>;
}
